import { getSearchableIdentifiers } from 'core/slices/utils/taskIdentifiers';
import { useCallback, useState } from 'react';
import { useDebouncedCallback } from '../../../hooks/useDebouncedCallback';

const SEARCH_DEBOUNCE_MS = 250;

/**
 * Manages the local search state for the task list.
 * Filters tasks by description, item code, or EAN/barcode against the full
 * pre-fetched list. Identifiers are resolved through getSearchableIdentifiers
 * so the promotion fallback (store_info.associated_item / associated_ean) is
 * applied the same way as the details view — letting store teams find products
 * by barcode when the item value is 0/absent.
 * Dispatches the filtered results back to Redux via setTasksFilter.
 *
 * The filter pass runs debounced (see SEARCH_DEBOUNCE_MS) because the full
 * list can be 5k–10k items — filtering on every keystroke caused noticeable
 * lag on mid-range devices.
 */
export function useSearchState(tasksFilterSearchList, tasksList, setTasksFilter) {
  const [search, setSearch] = useState('');
  const [searching, setSearching] = useState(false);

  const applySearch = useCallback((text) => {
    if (text) {
      const searchText = text.toUpperCase();
      const results = (tasksFilterSearchList || []).filter((task) => {
        const desc = task.description ? task.description.toUpperCase() : '';
        const identifiers = getSearchableIdentifiers(task);
        return desc.includes(searchText) || identifiers.some((id) => id.includes(searchText));
      });
      setTasksFilter(results);
    } else {
      setTasksFilter(tasksList);
    }
  }, [tasksFilterSearchList, tasksList, setTasksFilter]);

  const debouncedApply = useDebouncedCallback(applySearch, SEARCH_DEBOUNCE_MS);

  const handleSearch = useCallback((text) => {
    setSearch(text);
    // `searching` flips synchronously — it gates pagination and UI counters,
    // so it must reflect intent the moment the user types, not after debounce.
    setSearching(!!text);
    debouncedApply(text);
  }, [debouncedApply]);

  const resetSearch = useCallback(() => {
    debouncedApply.cancel();
    setSearch('');
    setSearching(false);
    setTasksFilter(tasksList);
  }, [debouncedApply, setTasksFilter, tasksList]);

  return { search, searching, handleSearch, resetSearch };
}
